import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Home, Building, CheckCircle2, AlertTriangle,
  CloudRain, Sun, Snowflake, ShieldAlert,
  HeartHandshake
} from 'lucide-react';

const EspacioYEntornoCard = ({ espacioYEntorno }) => {
  if (!espacioYEntorno) return null;
  const { espacioIdeal, climatologia, seguridadHogar } = espacioYEntorno;
  const SectionHeader = ({ icon: Icon, title, description }) => (
    <div className="space-y-3">
      {/* Title Group */}
      <div className="flex items-center gap-3">
        {/* Icon wrapper without gradient background */}
        <Icon
          className="h-8 w-8 text-emerald-600 flex-shrink-0"
          strokeWidth={2}
        />

        {/* Title Text */}
        <h2 className="text-[1.35rem] font-semibold text-gray-900 leading-tight">
          {title}
        </h2>
      </div>

      {/* Description with proper text size and spacing */}
      {description && (
        <p className="text-[0.95rem] text-gray-600 leading-relaxed ml-[2.75rem]">
          {description}
        </p>
      )}

      {/* Subtle divider */}
      <div className="h-px bg-gradient-to-r from-emerald-100/80 via-emerald-100/50 to-transparent" />
    </div>
  );

  const InfoBox = ({ icon: Icon, title, content, color = "emerald", className = "" }) => (
    <div className={`bg-white rounded-2xl border border-${color}-100 p-5 hover:shadow-xl transition-all duration-300 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 bg-gradient-to-br from-${color}-400 to-${color}-500 rounded-lg shadow-md`}>
          <Icon className="h-4 w-4 text-white" strokeWidth={1.5} />
        </div>
        <h4 className={`text-lg font-semibold text-${color}-700`}>{title}</h4>
      </div>
      {content}
    </div>
  );

  const ListItem = ({ icon: Icon, text, color = "emerald", isHighlighted = false }) => (
    <div className={`flex items-start gap-3 ${isHighlighted ? `bg-${color}-50` : ''} p-3 rounded-xl transition-colors duration-200 hover:bg-${color}-50/80`}>
      <Icon className={`h-5 w-5 mt-0.5 text-${color}-500 flex-shrink-0`} strokeWidth={1.5} />
      <p className="text-gray-700 text-base leading-relaxed">{text}</p>
    </div>
  );

  const ToleranceDisplay = ({ title, level, items, icon: Icon, color }) => (
    <div className="bg-white rounded-lg p-3 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 text-${color}-500`} />
          <span className="font-medium text-gray-700 text-sm sm:text-base">{title}</span>
        </div>
        <span className={`inline-flex text-sm font-medium text-${color}-600 bg-${color}-100 px-3 py-1 rounded-full`}>
          {level}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <ListItem
            key={idx}
            icon={CheckCircle2}
            text={item}
            color={color}
            isHighlighted={idx % 2 === 0}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Card className="w-full max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-white shadow-lg overflow-hidden">
      <CardContent className="p-4 sm:p-6 lg:p-8">
        <div className="space-y-6 sm:space-y-8">
          <SectionHeader
            icon={HeartHandshake}
            title="Espacio y Entorno"
            description="Asegura un ambiente cómodo y seguro para tu mascota"
          />

          <div className="space-y-6 sm:space-y-8">
            {/* Espacio Ideal Section */}
            <InfoBox
              icon={Building}
              title="Espacio Ideal"
              color="emerald"
              className="w-full"
              content={
                <div className="space-y-4 sm:space-y-6">
                  <div className="bg-emerald-50/50 p-3 sm:p-4 rounded-xl">
                    <p className="text-[0.95rem] sm:text-base font-medium text-gray-700">
                      {espacioIdeal.tipoVivienda}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2.5">
                      {espacioIdeal.ventajas.map((ventaja, idx) => (
                        <ListItem
                          key={idx}
                          icon={CheckCircle2}
                          text={ventaja}
                          color="emerald"
                          isHighlighted
                          className="text-[0.95rem] sm:text-base"
                        />
                      ))}
                    </div>
                    <div className="space-y-2.5">
                      {espacioIdeal.adaptaciones.map((adaptacion, idx) => (
                        <ListItem
                          key={idx}
                          icon={AlertTriangle}
                          text={adaptacion}
                          color="emerald"
                          className="text-[0.95rem] sm:text-base"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              }
            />

            {/* Climate and Safety Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Climatología Box */}
              <InfoBox
                icon={Sun}
                title="Climatología"
                color="blue"
                className="w-full"
                content={
                  <div className="space-y-4 sm:space-y-5">
                    <ToleranceDisplay
                      title="Tolerancia al Frío"
                      level={climatologia.toleranciaFrio.nivel}
                      items={climatologia.toleranciaFrio.soluciones}
                      icon={Snowflake}
                      color="blue"
                      className="text-[0.95rem] sm:text-base"
                    />
                    <ToleranceDisplay
                      title="Tolerancia al Calor"
                      level={climatologia.toleranciaCalor.nivel}
                      items={climatologia.toleranciaCalor.precauciones}
                      icon={CloudRain}
                      color="blue"
                      className="text-[0.95rem] sm:text-base"
                    />
                  </div>
                }
              />

              {/* Seguridad Box */}
              <InfoBox
                icon={ShieldAlert}
                title="Seguridad en el Hogar"
                color="rose"
                className="w-full"
                content={
                  <div className="space-y-4 sm:space-y-5">
                    <div className="space-y-2.5">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 text-[0.95rem] sm:text-base">
                        <CheckCircle2 className="h-4 w-4 text-rose-500 flex-shrink-0" />
                        Adaptaciones Básicas
                      </h4>
                      {seguridadHogar.adaptaciones.basicas.map((item, idx) => (
                        <ListItem
                          key={idx}
                          icon={CheckCircle2}
                          text={item}
                          color="rose"
                          isHighlighted={idx % 2 === 0}
                          className="text-[0.95rem] sm:text-base"
                        />
                      ))}
                    </div>
                    <div className="space-y-2.5">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 text-[0.95rem] sm:text-base">
                        <AlertTriangle className="h-4 w-4 text-rose-500 flex-shrink-0" />
                        Adaptaciones Avanzadas
                      </h4>
                      {seguridadHogar.adaptaciones.avanzadas.map((item, idx) => (
                        <ListItem
                          key={idx}
                          icon={AlertTriangle}
                          text={item}
                          color="rose"
                          isHighlighted={idx % 2 === 0}
                          className="text-[0.95rem] sm:text-base"
                        />
                      ))}
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EspacioYEntornoCard;